import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { workCoverUrl } from "../lib/media";
import { useI18n } from "../contexts/I18nContext";
import useReveal from "../hooks/useReveal";
import {
    ArrowLeft,
    ArrowRight,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

const splitLines = (value) => String(value || "").split(/\n+/).map((line) => line.trim()).filter(Boolean);
const cleanBullet = (value) => value.replace(/^[\u2022\u00b7-]\s*/, "").trim();

export default function WorkDetailPage() {
    const { slug } = useParams();
    const { t, pick, isRTL } = useI18n();
    const ForwardArrow = isRTL ? ArrowLeft : ArrowRight;
    const BackChevron = isRTL ? ChevronRight : ChevronLeft;
    const ref = useReveal();
    const [work, setWork] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setWork(null);
        setNotFound(false);
        api.get(`/works/${slug}`).then((r) => setWork(r.data)).catch(() => setNotFound(true));
    }, [slug]);

    const narrative = useMemo(() => {
        if (!work) return [];
        return [
            { key: "objective", label: t("objective"), value: pick(work, "objective") },
            { key: "challenge", label: t("challenge"), value: pick(work, "challenge") },
            { key: "methodology", label: t("methodology"), value: pick(work, "methodology") },
            { key: "achievements", label: t("achievements"), value: pick(work, "achievements") },
        ].filter((item) => item.value);
    }, [work, pick, t]);

    if (notFound) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center bg-[#EFF0F1]" data-testid="work-not-found">
                <div className="text-center px-6">
                    <p className="heading text-3xl text-[#002951]">{t("not_found")}</p>
                    <Link to="/work" className="legends-link inline-flex items-center gap-2 mt-6 text-[#0060A7]">
                        <BackChevron size={18} />
                        {t("cta_view_all")}
                    </Link>
                </div>
            </div>
        );
    }

    if (!work) {
        return <div className="py-40 text-center text-[#6B7280]">Loading...</div>;
    }

    const title = pick(work, "title");
    const cover = workCoverUrl(work);
    const category = pick(work, "category");
    const gallery = work.gallery || [];
    const partnerLogos = (work.partner_logos || []).slice(0, 4);
    const metaItems = [
        { k: "client_label", v: pick(work, "client") },
        { k: "city_label", v: pick(work, "city") },
        { k: "year_label", v: work.year },
        { k: "category_label", v: category },
    ].filter((item) => item.v);

    return (
        <div ref={ref} data-testid="work-detail-page" className="bg-white">
            <section className="bg-[#EFF0F1] py-20 md:py-28">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
                    <Link
                        to="/work"
                        className="mb-8 inline-flex w-fit items-center gap-2 text-sm uppercase tracking-[0.14em] text-[#002951] transition-colors hover:text-[#0060A7]"
                        data-testid="work-detail-back-top"
                    >
                        <BackChevron size={16} />
                        {t("cta_view_all")}
                    </Link>

                    <div className="section-eyebrow mb-5 plus-mark">
                        {[category, work.year].filter(Boolean).join(" · ")}
                    </div>
                    <h1 className="heading page-hero-oneline max-w-5xl">
                        {title}
                    </h1>
                </div>
            </section>

            {cover && (
                <section className="bg-white pt-12 md:pt-16">
                    <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
                        <div className="aspect-[16/9] overflow-hidden bg-[#EFF0F1]">
                            <img
                                src={cover}
                                alt={title}
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                                className="h-full w-full object-contain"
                            />
                        </div>
                        {partnerLogos.length > 0 && (
                            <div className="mt-6 flex flex-wrap items-center gap-5" data-testid="work-partner-logos" style={{ direction: "ltr" }}>
                                {partnerLogos.map((logo, i) => (
                                    <img key={i} src={fileUrl(logo)} alt="" loading="lazy" decoding="async" className="max-h-12 max-w-[140px] object-contain" />
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section className="bg-white py-14 md:py-20">
                <div className="mx-auto max-w-[1100px] px-6 lg:px-12">
                    {metaItems.length > 0 && (
                        <div className="mb-12 grid grid-cols-2 gap-x-8 gap-y-6 border-y border-black/10 py-6 md:grid-cols-4" data-testid="work-meta">
                            {metaItems.map((m) => (
                                <div key={m.k}>
                                    <div className="text-xs uppercase tracking-[0.14em] text-[#0060A7]">{t(m.k)}</div>
                                    <div className="mt-2 heading text-lg font-bold text-[#002951]">{m.v}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="space-y-12">
                        {narrative.map((item, index) => {
                            const lines = splitLines(item.value);
                            const asBullets = item.key === "methodology" || lines.length > 1;
                            return (
                                <article key={item.key} data-testid={`work-${item.key}`}>
                                    <div className="section-eyebrow mb-4 text-[#0060A7]">
                                        {String(index + 1).padStart(2, "0")}
                                    </div>
                                    <h2 className="heading text-2xl md:text-3xl text-[#002951] font-bold">
                                        {item.label}
                                    </h2>
                                    {asBullets ? (
                                        <div className="mt-5 grid gap-3 text-[#374151]">
                                            {lines.map((line, i) => (
                                                <div key={i} className="flex items-start gap-3">
                                                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-[#0060A7]" />
                                                    <span className="text-base md:text-lg leading-relaxed">{cleanBullet(line)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="mt-5 text-base md:text-lg leading-relaxed text-[#374151] whitespace-pre-line">
                                            {item.value}
                                        </p>
                                    )}
                                </article>
                            );
                        })}
                    </div>

                    {work.stats && work.stats.length > 0 && (
                        <div className="mt-14 grid grid-cols-2 gap-x-8 gap-y-6 border-t border-black/10 pt-8 md:grid-cols-4">
                            {work.stats.map((s, i) => (
                                <div key={i} data-testid={`work-stat-${i}`}>
                                    <div className="heading text-3xl font-bold text-[#002951]">{s.value}</div>
                                    <div className="mt-2 text-sm leading-snug text-[#6B7280]">{pick(s, "label")}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {gallery.length > 0 && (
                <section className="bg-white py-16 md:py-24">
                    <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
                        <div className="mb-8 flex items-end justify-between gap-6">
                            <h2 className="heading text-3xl font-bold text-[#002951] md:text-4xl">Gallery</h2>
                            <div className="h-px flex-1 bg-black/10" />
                        </div>
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            {gallery.map((g, i) => (
                                <div
                                    key={i}
                                    className="group overflow-hidden bg-[#EFF0F1]"
                                >
                                    <div className="aspect-[16/9]">
                                        <img
                                            src={fileUrl(g)}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                            className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-[#EFF0F1] py-12 md:py-16">
                <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-6 md:flex-row md:items-center md:justify-between lg:px-12">
                    <div>
                        <div className="text-sm text-[#6B7280]">{category || t("section_works_overline")}</div>
                        <h2 className="heading mt-1 text-2xl font-black text-[#002951]">{t("cta_explore")}</h2>
                    </div>
                    <Link
                        to="/work"
                        className="group inline-flex w-fit items-center gap-3 bg-[#002951] px-7 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0060A7]"
                        data-testid="work-detail-back-bottom"
                    >
                        {t("cta_view_all")}
                        <ForwardArrow size={18} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                </div>
            </section>
        </div>
    );
}

